import {
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  OnDestroy,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { NavService } from '../../services/nav-service';
import { ContextService } from '../../services/context-service';
import { SidePanelService } from '../../services/side-panel-service';
import { ScreenFullService } from '../../services/screenfull-service';
import { SpinnerService } from '../../services/spinner-service';

/**
 * Iframe shell: single light toolbar (icon + title + legacy header actions). No ShopperKit logo bar or client pill.
 * Subheader row matches legacy `HeaderComponent`.
 */
@Component({
  selector: 'sk-iframe-embed-header',
  templateUrl: './iframe-embed-header.component.html',
  styleUrls: ['./iframe-embed-header.component.scss'],
})
export class IframeEmbedHeaderComponent implements DoCheck, OnDestroy {
  @ViewChild('subHeader') private subHeader: ElementRef;

  showSubHeader = false;
  subHeaderTemplate: TemplateRef<any> = null;
  headerIsFullscreenAllowedTemp: boolean;
  /** Font Awesome classes for the secondary row; aligned with `home.html` tile icons. */
  sectionIconClass = 'fa fa-fw fa-home';

  constructor(
    public nav: NavService,
    public contextService: ContextService,
    public sidePanel: SidePanelService,
    public screenFullService: ScreenFullService,
    private readonly spinnerService: SpinnerService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly elementRef: ElementRef,
  ) {
    this.nav.headerChangeDetector = this.cdr;
    this.sidePanel.headerChangeDetector = this.cdr;
    this.nav.iframeEmbedToolbarSuppressed = !this.showToolbarChrome;
  }

  ngDoCheck() {
    if (this.nav.headerIsFullscreenAllowed !== this.headerIsFullscreenAllowedTemp) {
      this.headerIsFullscreenAllowedTemp = this.nav.headerIsFullscreenAllowed;
    }
    this.sectionIconClass = this.resolveSectionIconClass(this.router.url);
    this.showSubHeader = this.nav.subHeaderIsVisible();
    this.subHeaderTemplate = this.nav.getSubHeaderTemplate();
    this.nav.offsetTop =
      (this.subHeader && this.subHeader.nativeElement && this.subHeader.nativeElement.clientHeight) ||
      this.nav.subHeaderHeight ||
      0;
    const stackPx = this.elementRef.nativeElement.offsetHeight;
    this.nav.iframeEmbedHeaderStackPx = stackPx > 0 ? stackPx : 0;
    this.nav.iframeEmbedToolbarSuppressed = !this.showToolbarChrome;
  }

  ngOnDestroy() {
    this.nav.iframeEmbedHeaderStackPx = 0;
    this.nav.iframeEmbedToolbarSuppressed = false;
  }

  get title(): string {
    return this.nav.headerTitle || 'Command Center';
  }

  get showToolbarChrome(): boolean {
    if (this.isAuthShellPath()) {
      return false;
    }
    if (!this.contextService.loggedIn) {
      return false;
    }
    if (this.spinnerService.isShowing) {
      return false;
    }
    return true;
  }

  private isAuthShellPath(): boolean {
    const pathOnly = (this.router.url || '').split('?')[0];
    const segments = pathOnly.split('/').filter(s => s.length > 0);
    const last = segments.length ? segments[segments.length - 1] : '';
    return last === 'login' || last === 'oauthlogin';
  }

  private resolveSectionIconClass(url: string): string {
    const path = (url || '').split('?')[0].toLowerCase();
    /* Match icons on `communications.html` tiles (not the single-bubble parent). */
    if (path.includes('/communications/chats')) {
      return 'fa fa-fw fa-comments-o';
    }
    if (path.includes('/communications/text-templates')) {
      return 'fa fa-fw fa-file-code-o';
    }
    if (path.includes('/communicationshub')) {
      return 'fa fa-fw fa-comments';
    }
    if (path.includes('/communications')) {
      return 'fa fa-fw fa-comments';
    }
    if (path.includes('/orders')) {
      return 'fa fa-fw fa-shopping-cart';
    }
    if (path.includes('/inventory')) {
      return 'fa fa-fw fa-server';
    }
    if (path.includes('/events')) {
      return 'fa fa-fw fa-calendar';
    }
    if (path.includes('/stagedorders')) {
      return 'fa fa-fw fa-shopping-basket';
    }
    if (path.includes('/orderarchives')) {
      return 'fa fa-archive';
    }
    if (path.includes('/home') || path === '/' || path === '') {
      return 'fa fa-fw fa-home';
    }
    return 'fa fa-fw fa-home';
  }
}
